import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteUser } from "../../../react-redux/Slicer/userSlice";
import { usePermissions } from "./T2TUIProvider";
import ProductSwitcher from "./ProductSwitcher";
// One brand mark for the whole app -- the same file the switcher renders for
// our own tile, so the sidebar, the login card and the panel can never drift.
import t2tLogo from "../../../assets/plot-os/t2t.svg";

const NAV_ITEMS = [
  { label: "Dashboard",    icon: "bi bi-grid-1x2-fill",          path: "/t2t-home",         requiredPerm: null },
  { label: "My Queue",     icon: "bi bi-inbox-fill",              path: "/t2t-queue",        requiredPerm: null, badge: true },
  { label: "Requests",     icon: "bi bi-layout-three-columns",   path: "/t2t/requests",     requiredPerm: "requests.view_all" },
  { label: "Reports",      icon: "bi bi-bar-chart-fill",          path: "/t2t-reports",      requiredPerm: "reports.view" },
  { label: "Vendors",      icon: "bi bi-building",                path: "/t2t-vendors",      requiredPerm: null },
  { label: "Integrations", icon: "bi bi-plug-fill",               path: "/t2t-integrations", requiredPerm: "users.manage" },
  { label: "Org Setup",    icon: "bi bi-diagram-3-fill",          path: "/t2t-org",          requiredPerm: "org.manage" },
  { label: "Admin",        icon: "bi bi-shield-lock-fill",        path: "/t2t-admin",        requiredPerm: "users.manage" },
  { label: "User Management", icon: "bi bi-people-fill",          path: "/view-users",       requiredPerm: "users.manage" },
];

const T2TSidebar = ({ queueCount = 0, onClose }) => {
  const navigate       = useNavigate();
  const location       = useLocation();
  const dispatch       = useDispatch();
  const user           = useSelector((state) => state?.user?.users[0]);
  const { hasPermission } = usePermissions();
  // The switcher panel is portalled outside this <aside>, so the sidebar must
  // stop auto-closing on mouse-leave while it is open -- otherwise moving the
  // pointer to the panel tears it down.
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
    : "T2";

  const handleLogout = () => {
    // Expire the switcher cookie server-side. T2T's logout is otherwise purely
    // client-side, which would leave /plot/switch usable for the cookie's full
    // 7 days. Best-effort: we clear local state regardless of the outcome.
    fetch("/plot/logout", { method: "POST", credentials: "include" }).catch(() => {});

    // BUG-75: preserve login_type across logout so the Login page can land on
    // the T2T tab instead of defaulting to OSIF.
    const loginType = localStorage.getItem("login_type");
    localStorage.clear();
    sessionStorage.clear();
    if (loginType) localStorage.setItem("login_type", loginType);
    dispatch(deleteUser());
    navigate("/");
  };

  const handleNavClick = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  return (
    <aside
      id="t2t-sidebar"
      className="t2t-sidebar"
      onMouseLeave={switcherOpen ? undefined : onClose}
    >
      {/* Logo + PLOT OS switcher row */}
      <div className="t2t-sidebar-brand">
        <div className="t2t-sidebar-logo">
          <img src={t2tLogo} alt="" className="t2t-sidebar-logo-icon" />
          <div>
            <div className="t2t-sidebar-logo-text">T2T</div>
            <span className="t2t-sidebar-logo-sub">Table to Table</span>
          </div>
        </div>
        <ProductSwitcher onOpenChange={setSwitcherOpen} />
      </div>

      {/* User + Logout */}
      <div className="t2t-sidebar-user">
        <div className="t2t-sidebar-user-info">
          <div className="t2t-avatar">{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t2t-user-name">Hi, {user?.name || "T2T User"}</div>
          </div>
          <button
            onClick={handleLogout}
            title="Logout"
            style={{ background: "none", border: "none", cursor: "pointer", color: "#FCA5A5", fontSize: 18, padding: "2px 4px", flexShrink: 0 }}
          >
            <i className="bi bi-power"></i>
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="t2t-sidebar-nav">
        {NAV_ITEMS.filter((item) => !item.requiredPerm || hasPermission(item.requiredPerm)).map((item) => {
          const isActive = location.pathname === item.path ||
            (item.path !== "/t2t-home" && location.pathname.startsWith(item.path));
          return (
            <button
              key={item.path}
              className={`t2t-nav-item${isActive ? " active" : ""}`}
              onClick={() => handleNavClick(item.path)}
            >
              <span className="t2t-nav-icon"><i className={item.icon}></i></span>
              {item.label}
              {item.badge && queueCount > 0 && (
                <span className="t2t-nav-badge">{queueCount}</span>
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
};

T2TSidebar.propTypes = {
  queueCount: PropTypes.number,
  onClose:    PropTypes.func,
};

export default T2TSidebar;
